import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from "rxjs/operators";
import { AngularFirestore, DocumentData, QueryFn } from '@angular/fire/firestore';
import { DataService } from '../data.service';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from 'src/environments/environment';
import { ResultConfigViewModel } from 'src/app/models/results/result-config-view-model';
import { CdwInventoryItem } from 'src/app/models/data/cdw/cdw-inventory-item';
import { constants } from 'src/environments/constants';
import { ILocaleInfo, UtilityService } from '../../utility.service';

@Injectable({
  providedIn: 'root'
})
export class CdwDataService implements DataService {

  constructor(
    private store: AngularFirestore,
    private auth: AngularFireAuth,
    private utilityService: UtilityService) { }

  updateResultConfigItem(item: ResultConfigViewModel): Promise<void> {
    return new Promise((resolve, reject) => {
      resolve();
    });
  }

  getAdminEmailAddresses(): Observable<string[]> {
    return of([]);
  }

  executeDatabaseQuery(): Observable<InventoryItemDTO[]> {
    const localeInfo: ILocaleInfo = this.utilityService.getLocaleInfo();

    const queryFn: QueryFn<DocumentData> = ref => ref.where('Locality', '==', localeInfo.localeName).where('Deleted', '==', false);

    return this.auth.user.pipe(
      take(1),
      switchMap(async user => {
        if (!user) {
          await this.auth.signInWithEmailAndPassword(environment.firebaseUserEmail, environment.firebase.apiKey);
        }
      }),
      switchMap(() => this.store.collection<CdwInventoryItem>(`${constants.FIRESTORE_INVENTORY_COLLECTION}_cdw`, queryFn).valueChanges()),
      take(1),
      map((cdwItems: CdwInventoryItem[]) => {
        return cdwItems.map(cdwItem => this.mapDTO(cdwItem));
      })
    );
  }

  mapDTO(cdwItem: CdwInventoryItem): InventoryItemDTO {
    return Object.assign(new InventoryItemDTO(), {
      id: cdwItem.ID,
      hasBacklitKeyboard: cdwItem.BacklitKeyboard,
      hasFingerprintReader: cdwItem.HasFingerprintReader,
      hasPrivacyScreen: cdwItem.HasPrivacyScreen,
      hasStylus: cdwItem.HasStylus,
      cpuModel: cdwItem.CpuModel,
      diskDriveSize: cdwItem.DiskDriveSize,
      diskDriveUnit: cdwItem.DiskDriveUnit,
      diskDriveType: cdwItem.DiskDriveType,
      hasTouchscreen: cdwItem.HasTouchscreen,
      imageLargeLocation: cdwItem.ImageLarge,
      isEducation: cdwItem.IsEducation,
      isParallelsCapable: cdwItem.IsParallelsCapable,
      listPrice: CdwInventoryItem.getPrice(cdwItem),
      locality: cdwItem.Locality,
      manufactureName: cdwItem.ManufactureName,
      manufacturePart: cdwItem.ManufacturePart,
      materialID: cdwItem.MaterialID,
      memorySize: cdwItem.MemorySize,
      memoryUnit: cdwItem.MemoryUnit,
      name: cdwItem.ProductDescShort.split(' - ')[0],
      productDescLong: cdwItem.ProductDescLong,
      productDescShort: cdwItem.ProductDescShort,
      productLine: cdwItem.ProductLine,
      productType: cdwItem.ProductType,
      formattedScreenSize: cdwItem.ScreenSize,
      screenSize: CdwInventoryItem.getScreenSize(cdwItem),
      showInResults: cdwItem.ShowInResults,
      vendorURL: cdwItem.VendorURL
    });
  }
}
